import React from "react";
import {
  Box, Checkbox, Divider, FormControl, FormControlLabel,
  FormGroup, FormLabel, Grid, Radio, RadioGroup,
  TextField, Typography,
} from "@mui/material";

const STROKE_LOCATION_OPTIONS = [
  "Cortical",
  "Subcortical",
  "Mesencéfalo",
  "Tronco encefálico",
];

const STROKE_LOCATION_DETAIL_OPTIONS = [
  "Internal capsule",
  "Middle cerebral artery",
  "Lobe",
];

const VISUAL_IMPAIRMENT_OPTIONS = [
  { value: "0", label: "0 — Ninguna" },
  { value: "1", label: "1 — Pérdida de campo visual (hemianopsia, cuadrantanopsia, etc.)" },
  { value: "2", label: "2 — Alteración de la motilidad ocular / estrabismo" },
  { value: "3", label: "3 — Disminución de la agudeza visual central" },
  { value: "4", label: "4 — Trastorno perceptivo visual o inatención visual" },
  { value: "5", label: "5 — Desconocido" },
];

function SectionTitle({ children }) {
  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mt: 1, mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {children}
      </Typography>
      <Divider />
    </Grid>
  );
}

function CheckboxGroup({ options, selected, onChange, valueKey }) {
  const toggle = (val) => {
    const next = selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val];
    onChange(next);
  };

  return (
    <FormGroup>
      <Grid container>
        {options.map((opt) => {
          const val   = valueKey ? opt[valueKey] : opt;
          const label = valueKey ? opt.label     : opt;
          return (
            <Grid item xs={12} sm={6} key={val}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.includes(val)}
                    onChange={() => toggle(val)}
                    size="small"
                  />
                }
                label={label}
              />
            </Grid>
          );
        })}
      </Grid>
    </FormGroup>
  );
}

export default function Diagnosis({ form, onChange, onCheckboxChange }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>
        Diagnóstico
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={2.5}>

        {/* ── Datos del ictus ── */}
        <SectionTitle>Datos del ictus</SectionTitle>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Tiempo desde el ictus (meses)"
            name="time_since_stroke_months"
            type="number"
            value={form.time_since_stroke_months}
            onChange={onChange}
            fullWidth
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="NIHSS (0–42)"
            name="nihss"
            type="number"
            value={form.nihss}
            onChange={onChange}
            fullWidth
            inputProps={{ min: 0, max: 42 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <FormLabel>Tipo de lesión por ictus</FormLabel>
            <RadioGroup
              row
              name="stroke_type"
              value={form.stroke_type}
              onChange={onChange}
            >
              <FormControlLabel value="isquemico"   control={<Radio />} label="Isquémico" />
              <FormControlLabel value="hemorragico" control={<Radio />} label="Hemorrágico" />
              <FormControlLabel value="AIT"         control={<Radio />} label="AIT" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl>
            <FormLabel>Estado del ictus</FormLabel>
            <RadioGroup
              name="stroke_status"
              value={form.stroke_status}
              onChange={onChange}
            >
              <FormControlLabel value="subagudo_temprano" control={<Radio />} label="Subagudo temprano (0–3 meses)" />
              <FormControlLabel value="subagudo_tardio"   control={<Radio />} label="Subagudo tardío (3–6 meses)" />
              <FormControlLabel value="cronico"           control={<Radio />} label="Crónico (> 6 meses)" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl>
            <FormLabel>Lado afectado</FormLabel>
            <RadioGroup
              row
              name="affected_side"
              value={form.affected_side}
              onChange={onChange}
            >
              <FormControlLabel value="derecho"   control={<Radio />} label="Derecho" />
              <FormControlLabel value="izquierdo" control={<Radio />} label="Izquierdo" />
              <FormControlLabel value="bilateral" control={<Radio />} label="Bilateral" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Localización del ictus</FormLabel>
            <CheckboxGroup
              options={STROKE_LOCATION_OPTIONS}
              selected={form.stroke_location}
              onChange={(val) => onCheckboxChange("stroke_location", val)}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Detalle de localización</FormLabel>
            <CheckboxGroup
              options={STROKE_LOCATION_DETAIL_OPTIONS}
              selected={form.stroke_location_detail}
              onChange={(val) => onCheckboxChange("stroke_location_detail", val)}
            />
          </FormControl>
        </Grid>

        {/* ── Neglect ── */}
        <SectionTitle>Neglect</SectionTitle>

        <Grid item xs={12} sm={4}>
          <FormControl>
            <FormLabel>¿Neglect presente?</FormLabel>
            <RadioGroup
              row
              name="neglect_present"
              value={form.neglect_present === null ? "" : String(form.neglect_present)}
              onChange={(e) =>
                onChange({ target: { name: "neglect_present", value: e.target.value === "true" } })
              }
            >
              <FormControlLabel value="false" control={<Radio />} label="No" />
              <FormControlLabel value="true"  control={<Radio />} label="Sí" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={8}>
          <FormControl disabled={!form.neglect_present}>
            <FormLabel>Lado espacial del neglect</FormLabel>
            <RadioGroup
              row
              name="neglect_side"
              value={form.neglect_side}
              onChange={onChange}
            >
              <FormControlLabel value="derecho"   control={<Radio />} label="Derecho" />
              <FormControlLabel value="izquierdo" control={<Radio />} label="Izquierdo" />
              <FormControlLabel value="bilateral" control={<Radio />} label="Bilateral" />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* ── Deterioro visual ── */}
        <SectionTitle>Deterioro visual</SectionTitle>

        <Grid item xs={12} sm={4}>
          <FormControl>
            <FormLabel>¿Deterioro visual presente?</FormLabel>
            <RadioGroup
              row
              name="visual_impairment_present"
              value={form.visual_impairment_present}
              onChange={onChange}
            >
              <FormControlLabel value=""           control={<Radio />} label="No" />
              <FormControlLabel value="derecho"   control={<Radio />} label="Derecho" />
              <FormControlLabel value="izquierdo" control={<Radio />} label="Izquierdo" />
              <FormControlLabel value="bilateral" control={<Radio />} label="Bilateral" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={8}>
          <FormControl component="fieldset" fullWidth disabled={!form.visual_impairment_present}>
            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Tipo de deterioro visual</FormLabel>
            <CheckboxGroup
              options={VISUAL_IMPAIRMENT_OPTIONS}
              selected={form.visual_impairment_type}
              onChange={(val) => onCheckboxChange("visual_impairment_type", val)}
              valueKey="value"
            />
          </FormControl>
        </Grid>

        {/* ── Evaluación cognitiva ── */}
        <SectionTitle>Evaluación cognitiva</SectionTitle>

        <Grid item xs={12} sm={4}>
          <TextField
            label="MoCA (0–30)"
            name="moca"
            type="number"
            value={form.moca}
            onChange={onChange}
            fullWidth
            inputProps={{ min: 0, max: 30 }}
          />
        </Grid>

        <Grid item xs={12} sm={8}>
          <TextField
            label="Depresión / Ansiedad"
            name="depression_anxiety"
            value={form.depression_anxiety}
            onChange={onChange}
            fullWidth
            multiline
            rows={3}
          />
        </Grid>

      </Grid>
    </Box>
  );
}
